import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FaWhatsapp } from "react-icons/fa";
import api from "../../../../services/api";

interface LinhaConsumo {
    idEmpresa: string;
    nomeEmpresa?: string | null;
    nivel?: number | null;
    nivelNome?: string | null;
    modelo?: number | null;
    modeloNome?: string | null;
    modelosQueResponderam?: string | null;
    mensagens: number;
    audios: number;
    anexos: number;
    chamadasModelo: number;
    tokensEntrada: number;
    tokensSaida: number;
    tokensCacheCriacao: number;
    tokensCacheLeitura: number;
    custoUsd: number;
    custoBrl: number;
}

interface RespostaConsumo {
    inicio: string;
    fim: string;
    precos: { modelo: number; nome: string; id: string; usdEntradaPorMilhao: number; usdSaidaPorMilhao: number; fatorCacheLeitura: number }[];
    cotacaoDolar: number;
    totais: { mensagens: number; tokensEntrada: number; tokensSaida: number; custoUsd: number; custoBrl: number };
    empresas: LinhaConsumo[];
}

const mesAtual = () => {
    const hoje = new Date();
    return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}`;
};

const inteiro = (n: number) => (n ?? 0).toLocaleString("pt-BR");
const usd = (n: number) => `US$ ${(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
const brl = (n: number) => (n ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const celula: React.CSSProperties = { padding: "8px 10px", borderBottom: "1px solid #e5e9f0", fontSize: "0.85rem", whiteSpace: "nowrap" };
const cabecalho: React.CSSProperties = { ...celula, background: "#f3f6fb", color: "#1F467E", fontWeight: 600, textAlign: "right" };

/** Consumo de IA do assistente no WhatsApp por empresa num mês, com custo estimado pela tabela do appsettings. */
export default function ConsumoAssistente() {
    const [mes, setMes] = useState(mesAtual());
    const [dados, setDados] = useState<RespostaConsumo | null>(null);
    const [carregando, setCarregando] = useState(false);

    const carregar = useCallback(async (mesEscolhido: string) => {
        const [ano, m] = mesEscolhido.split("-").map(Number);
        if (!ano || !m) return;
        const inicio = `${ano}-${String(m).padStart(2, "0")}-01`;
        const ultimoDia = new Date(ano, m, 0).getDate();
        const fim = `${ano}-${String(m).padStart(2, "0")}-${String(ultimoDia).padStart(2, "0")}`;
        setCarregando(true);
        try {
            const res = await api.get<RespostaConsumo>(`/AssistenteWhatsApp/admin/consumo`, { params: { inicio, fim } });
            setDados(res.data);
        } catch (error: any) {
            toast.error(error.response?.data?.Mensagem || "Erro ao carregar o consumo do assistente");
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => { carregar(mes); }, [mes, carregar]);

    return (
        <div style={{ padding: "8px 4px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: "1.05rem", color: "#1F467E", display: "flex", alignItems: "center", gap: 8 }}>
                    <FaWhatsapp /> Consumo do assistente por empresa
                </h3>
                <input
                    type="month"
                    value={mes}
                    onChange={(e) => setMes(e.target.value)}
                    style={{ padding: "6px 10px", border: "1px solid #d7dee8", borderRadius: 6, fontSize: "0.9rem" }}
                />
                {dados && (
                    <span style={{ fontSize: "0.8rem", color: "#64748b" }}>
                        Preço por milhão de tokens: {dados.precos.map((p) => `${p.nome} US$ ${p.usdEntradaPorMilhao} entrada / US$ ${p.usdSaidaPorMilhao} saída`).join("; ")}; dólar R$ {dados.cotacaoDolar}.
                        O custo é calculado pelo modelo que respondeu cada mensagem. Estimativa; o valor real é o da fatura da Anthropic.
                    </span>
                )}
            </div>

            {carregando && <p style={{ fontSize: "0.9rem", color: "#64748b" }}>Carregando...</p>}

            {!carregando && dados && dados.empresas.length === 0 && (
                <p style={{ fontSize: "0.9rem", color: "#64748b" }}>Nenhuma conversa com IA registrada no período.</p>
            )}

            {!carregando && dados && dados.empresas.length > 0 && (
                <div style={{ overflowX: "auto", border: "1px solid #e5e9f0", borderRadius: 8 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                            <tr>
                                <th style={{ ...cabecalho, textAlign: "left" }}>Empresa</th>
                                <th style={{ ...cabecalho, textAlign: "left" }}>Modo</th>
                                <th style={{ ...cabecalho, textAlign: "left" }}>Modelo</th>
                                <th style={cabecalho}>Mensagens</th>
                                <th style={cabecalho}>Áudios</th>
                                <th style={cabecalho}>Anexos</th>
                                <th style={cabecalho}>Chamadas IA</th>
                                <th style={cabecalho}>Tokens entrada</th>
                                <th style={cabecalho}>Cache lido</th>
                                <th style={cabecalho}>Tokens saída</th>
                                <th style={cabecalho}>Custo US$</th>
                                <th style={cabecalho}>Custo R$</th>
                                <th style={cabecalho}>R$ por msg</th>
                            </tr>
                        </thead>
                        <tbody>
                            {dados.empresas.map((e) => (
                                <tr key={e.idEmpresa}>
                                    <td style={celula}>{e.nomeEmpresa || e.idEmpresa}</td>
                                    <td style={celula}>{e.nivelNome || "Desligado"}</td>
                                    <td style={celula} title={e.modelosQueResponderam ? `Respondeu com: ${e.modelosQueResponderam}` : undefined}>
                                        {e.modeloNome || "-"}
                                        {e.modelosQueResponderam && e.modelosQueResponderam !== e.modeloNome && (
                                            <span style={{ color: "#64748b" }}> (mês: {e.modelosQueResponderam})</span>
                                        )}
                                    </td>
                                    <td style={{ ...celula, textAlign: "right" }}>{inteiro(e.mensagens)}</td>
                                    <td style={{ ...celula, textAlign: "right" }}>{inteiro(e.audios)}</td>
                                    <td style={{ ...celula, textAlign: "right" }}>{inteiro(e.anexos)}</td>
                                    <td style={{ ...celula, textAlign: "right" }}>{inteiro(e.chamadasModelo)}</td>
                                    <td style={{ ...celula, textAlign: "right" }}>{inteiro(e.tokensEntrada + e.tokensCacheCriacao)}</td>
                                    <td style={{ ...celula, textAlign: "right" }}>{inteiro(e.tokensCacheLeitura)}</td>
                                    <td style={{ ...celula, textAlign: "right" }}>{inteiro(e.tokensSaida)}</td>
                                    <td style={{ ...celula, textAlign: "right" }}>{usd(e.custoUsd)}</td>
                                    <td style={{ ...celula, textAlign: "right", fontWeight: 600 }}>{brl(e.custoBrl)}</td>
                                    <td style={{ ...celula, textAlign: "right" }}>{e.mensagens > 0 ? brl(e.custoBrl / e.mensagens) : "-"}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td style={{ ...celula, fontWeight: 600 }} colSpan={3}>Total</td>
                                <td style={{ ...celula, textAlign: "right", fontWeight: 600 }}>{inteiro(dados.totais.mensagens)}</td>
                                <td style={celula} colSpan={3} />
                                <td style={{ ...celula, textAlign: "right", fontWeight: 600 }}>{inteiro(dados.totais.tokensEntrada)}</td>
                                <td style={celula} />
                                <td style={{ ...celula, textAlign: "right", fontWeight: 600 }}>{inteiro(dados.totais.tokensSaida)}</td>
                                <td style={{ ...celula, textAlign: "right", fontWeight: 600 }}>{usd(dados.totais.custoUsd)}</td>
                                <td style={{ ...celula, textAlign: "right", fontWeight: 600 }}>{brl(dados.totais.custoBrl)}</td>
                                <td style={{ ...celula, textAlign: "right", fontWeight: 600 }}>
                                    {dados.totais.mensagens > 0 ? brl(dados.totais.custoBrl / dados.totais.mensagens) : "-"}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </div>
    );
}
